import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatCurrency } from '../lib/format';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Package, Plus } from 'lucide-react';

export function TemplateSelector({ open, onClose, documentDate, onSelectTemplates }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    if (open) {
      fetchTemplates();
      setSelectedIds(new Set());
    }
  }, [open]);

  const fetchTemplates = () => {
    setLoading(true);
    api.get('/line-item-templates?is_active=true')
      .then(r => {
        setTemplates(r.data);
        setLoading(false);
      })
      .catch(() => {
        toast.error('Failed to load templates');
        setLoading(false);
      });
  };

  const categories = [...new Set(templates.map(t => t.category))];
  const filteredTemplates = categoryFilter === 'all' 
    ? templates 
    : templates.filter(t => t.category === categoryFilter);

  const handleToggleSelection = (templateId) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(templateId)) {
      newSet.delete(templateId);
    } else {
      newSet.add(templateId);
    }
    setSelectedIds(newSet);
  };

  const handleAddTemplates = () => {
    const selectedTemplates = templates.filter(t => selectedIds.has(t.id));
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one template');
      return;
    }

    // Convert templates to line item format (copy values, not link)
    const lineItems = selectedTemplates.map(t => ({
      description: t.name + (t.description ? ` - ${t.description}` : ''),
      quantity: String(t.default_quantity || 1),
      unit_price: String(t.default_unit_price || 0),
      service_date: documentDate || new Date().toISOString().split('T')[0], // Use documentDate from parent
      taxable_gst: t.taxable_gst !== undefined ? t.taxable_gst : true,
      taxable_pst: t.taxable_pst !== undefined ? t.taxable_pst : false,
      item_type: t.category.toLowerCase() === 'labor' ? 'labor' : 
                 t.category.toLowerCase() === 'material' || t.category.toLowerCase() === 'materials' ? 'material' : 
                 'other', // For estimates
    }));

    onSelectTemplates(lineItems);
    toast.success(`Added ${lineItems.length} template(s) to line items`);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Add from Templates</DialogTitle>
        </DialogHeader>

        <div className="flex gap-3 mb-4">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48" data-testid="template-selector-category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-sm text-slate-500 pt-2">
            {selectedIds.size} selected
          </p>
        </div>

        <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading templates...</div>
          ) : filteredTemplates.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No active templates found</p>
              <p className="text-xs text-slate-400 mt-1">Create templates first on the Templates page</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead>Tax</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.map((t) => (
                  <TableRow 
                    key={t.id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleToggleSelection(t.id)}
                    data-testid={`template-row-${t.id}`}
                  >
                    <TableCell>
                      <Checkbox 
                        checked={selectedIds.has(t.id)}
                        onCheckedChange={() => handleToggleSelection(t.id)}
                        data-testid={`template-checkbox-${t.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{t.name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{t.category}</Badge></TableCell>
                    <TableCell className="text-slate-600 text-sm max-w-xs truncate">{t.description || '-'}</TableCell>
                    <TableCell className="text-right">{t.default_quantity} {t.unit}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(t.default_unit_price)}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {t.taxable_gst && 'GST'} {t.taxable_pst && 'PST'}
                      {!t.taxable_gst && !t.taxable_pst && '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            type="button" 
            className="bg-blue-600 hover:bg-blue-700" 
            onClick={handleAddTemplates}
            disabled={selectedIds.size === 0}
            data-testid="add-templates-submit"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add {selectedIds.size > 0 ? `${selectedIds.size}` : ''} Template{selectedIds.size !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
